import { BUDGET_USER, DECISION_USER } from 'business/constants';
import { ScopeT, UserRole } from 'business/definitions';
import { FINANCE_SCOPE } from 'routes/BudgetModule/config/constants';
import { AccessTypeButtonItem } from '../components/PermissionEditor/PermissionEditor';
import { getBlocks } from 'helpers/scopes';

export const shouldDisplayScope = (scope: ScopeT, roles: UserRole[]) => {
    if (scope.name === FINANCE_SCOPE) {
        return roles.includes(DECISION_USER) || roles.includes(BUDGET_USER);
    }
    return roles.includes(DECISION_USER);
};

/**
 * Get the path to the tooltip message when the scope is disabled in the edit user menu.
 * @param {ScopeT} scope - The scope configuration
 * @returns {string} - The path to the message to display
 */
export const getTooltipMessageForDisabledScope = (scope: ScopeT): string => {
    if (scope.name === FINANCE_SCOPE) {
        return 'adminSpace.editUser.modal.sideBar.disabledTooltip.defaultFinance';
    }
    return 'adminSpace.editUser.modal.sideBar.disabledTooltip.other';
};

/**
 * Get the access type buttons for a scope or a write scope.
 * @param {ScopeT} scope - The scope configuration
 * @param {string} restrictedTooltipKey - The tooltip translation key for restricted access
 * @param {string} noAccessTooltipKey - The tooltip translation key for no access
 * @returns {AccessTypeButtonItem[]} - The list of access type buttons
 */
const getAccessTypeButtons = (
    restrictedTooltipKey: string,
    noAccessTooltipKey: string,
    scope?: ScopeT,
): AccessTypeButtonItem[] => [
    {
        type: 'full_access',
    },
    {
        type: 'restricted',
        tooltipTitle: restrictedTooltipKey,
        hidden: Boolean(!scope?.expression),
    },
    {
        type: 'no_access',
        tooltipTitle: noAccessTooltipKey,
    },
];

/**
 * Get the access type buttons for a scope.
 * @param {ScopeT} scope - The scope configuration
 * @returns {AccessTypeButtonItem[]} - The list of access type buttons
 */
export const getScopeAccessTypeButton = (
    scope?: ScopeT,
): AccessTypeButtonItem[] =>
    getAccessTypeButtons(
        'adminSpace.createUser.modalStepper.scopeSection.restricted',
        'adminSpace.createUser.modalStepper.scopeSection.noAccess',
        scope,
    );

/**
 * Get the access type buttons for a write scope.
 * @param {ScopeT} scope - The scope configuration
 * @returns {AccessTypeButtonItem[]} - The list of access type buttons
 */
export const getWriteScopeAccessTypeButton = (
    scope?: ScopeT,
): AccessTypeButtonItem[] =>
    getAccessTypeButtons(
        'adminSpace.createUser.modalStepper.writeScopesSection.restricted',
        'adminSpace.createUser.modalStepper.writeScopesSection.noAccess',
        scope,
    );

/** Returns blocks from the scope filtered by allowed budget-related blocks. */
export const getBudgetWriteBlocks = (scope: ScopeT): string[] => {
    const allowedWriteBlocks = [
        'gestionnaire',
        'org_chart_finance',
        'direction',
    ]; // explo-AMP : this should be managed better (Shortcut ID : 28345)
    const blocks = getBlocks(scope);

    return blocks.filter((block) => allowedWriteBlocks.includes(block));
};
